import { useEffect, useState } from 'react';
import { AdminLayout } from '../components/admin-layout';
import {
  Card,
  Table,
  Th,
  Td,
  Input,
  Select,
  Pagination,
  Alert,
  Badge,
  Button,
} from '../components/ui';
import {
  getPeriodeHonorList,
  getLaporanHonorarium,
  downloadLaporanHonorariumCsv,
  type LaporanHonorariumRow,
  type LaporanHonorariumGrandTotal,
} from '../features/honorarium/api';
import { getSekolahList, getPegawaiList } from '../features/master/api';
import { RoleCode, type PeriodeHonor, type Sekolah, type PegawaiListItem } from '@app/shared';
import { useAuth } from '../features/auth/use-auth';

type SortBy =
  | 'nama'
  | 'nip_nik'
  | 'sekolah_nama'
  | 'honor_jp'
  | 'honor_kehadiran'
  | 'honor_jabatan'
  | 'honor_wali_kelas'
  | 'total_insentif'
  | 'total_honor';

function formatRupiah(value: number): string {
  return new Intl.NumberFormat('id-ID', {
    style: 'currency',
    currency: 'IDR',
    minimumFractionDigits: 0,
    maximumFractionDigits: 0,
  }).format(value);
}

const emptyGrandTotal: LaporanHonorariumGrandTotal = {
  honor_jp: 0,
  honor_kehadiran: 0,
  honor_jabatan: 0,
  honor_wali_kelas: 0,
  total_insentif: 0,
  total_honor: 0,
  jumlah_pegawai: 0,
};

export function LaporanHonorariumPage() {
  const { user } = useAuth();
  const isAdmin = user?.roles.includes(RoleCode.ADMIN) ?? false;
  const isKS = user?.roles.includes(RoleCode.KEPALA_SEKOLAH) ?? false;

  const [rows, setRows] = useState<LaporanHonorariumRow[]>([]);
  const [grandTotal, setGrandTotal] = useState<LaporanHonorariumGrandTotal>(emptyGrandTotal);
  const [meta, setMeta] = useState({ page: 1, limit: 20, total: 0 });
  const [loading, setLoading] = useState(false);
  const [exporting, setExporting] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const [periodeHonorId, setPeriodeHonorId] = useState<number | null>(null);
  const [sekolahId, setSekolahId] = useState<number | null>(null);
  const [pegawaiId, setPegawaiId] = useState<number | null>(null);
  const [search, setSearch] = useState('');
  const [sortBy, setSortBy] = useState<SortBy>('nama');
  const [sortDir, setSortDir] = useState<'asc' | 'desc'>('asc');

  const [periodes, setPeriodes] = useState<PeriodeHonor[]>([]);
  const [sekolahs, setSekolahs] = useState<Sekolah[]>([]);
  const [pegawais, setPegawais] = useState<PegawaiListItem[]>([]);

  useEffect(() => {
    const loadDropdowns = async () => {
      try {
        const [periodeResult, sekolahResult, pegawaiResult] = await Promise.all([
          getPeriodeHonorList({ limit: 100 }),
          getSekolahList({ limit: 100, status_aktif: true }),
          getPegawaiList({ limit: 500, status_aktif: true }),
        ]);
        setPeriodes(periodeResult.rows);
        setSekolahs(sekolahResult.rows);
        setPegawais(pegawaiResult.rows);

        if (periodeResult.rows.length > 0 && periodeResult.rows[0]) {
          setPeriodeHonorId(periodeResult.rows[0].id);
        }
      } catch (e) {
        console.error('Failed to load dropdowns:', e);
      }
    };
    loadDropdowns();
  }, []);

  const load = async (page = 1) => {
    if (!periodeHonorId) return;

    setLoading(true);
    setError(null);
    try {
      const result = await getLaporanHonorarium({
        periode_honor_id: periodeHonorId,
        sekolah_id: sekolahId ?? undefined,
        pegawai_id: pegawaiId ?? undefined,
        search: search || undefined,
        page,
        limit: 20,
        sort_by: sortBy,
        sort_dir: sortDir,
      });
      setRows(result.rows);
      setGrandTotal(result.grand_total);
      setMeta({ page: result.page, limit: result.limit, total: result.total });
    } catch (e) {
      setError(e instanceof Error ? e.message : 'Gagal memuat data laporan honorarium');
      setRows([]);
      setGrandTotal(emptyGrandTotal);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    if (periodeHonorId) {
      load(1);
    }
  }, [periodeHonorId, sekolahId, pegawaiId, search, sortBy, sortDir]);

  const handleExportCsv = async () => {
    if (!periodeHonorId) return;

    setExporting(true);
    setError(null);
    try {
      await downloadLaporanHonorariumCsv({
        periode_honor_id: periodeHonorId,
        sekolah_id: sekolahId ?? undefined,
        pegawai_id: pegawaiId ?? undefined,
        search: search || undefined,
        sort_by: sortBy,
        sort_dir: sortDir,
      });
    } catch (e) {
      setError(e instanceof Error ? e.message : 'Gagal mengunduh CSV');
    } finally {
      setExporting(false);
    }
  };

  return (
    <AdminLayout title="Laporan Honorarium">
      <Card>
        <div className="mb-6">
          <h2 className="text-xl font-semibold mb-4">Filter Laporan</h2>
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-4">
            <div>
              <label className="block text-sm font-medium mb-1">Periode Honor *</label>
              <Select
                value={periodeHonorId?.toString() ?? ''}
                onChange={(e: React.ChangeEvent<HTMLSelectElement>) =>
                  setPeriodeHonorId(Number(e.target.value) || null)
                }
                disabled={loading}
              >
                <option value="">Pilih Periode</option>
                {periodes.map((p) => (
                  <option key={p.id} value={p.id}>
                    {p.nama} ({p.kode_periode})
                  </option>
                ))}
              </Select>
            </div>

            {(isAdmin || isKS) && (
              <div>
                <label className="block text-sm font-medium mb-1">Sekolah</label>
                <Select
                  value={sekolahId?.toString() ?? ''}
                  onChange={(e: React.ChangeEvent<HTMLSelectElement>) =>
                    setSekolahId(Number(e.target.value) || null)
                  }
                  disabled={loading}
                >
                  <option value="">Semua Sekolah</option>
                  {sekolahs.map((s) => (
                    <option key={s.id} value={s.id}>
                      {s.nama}
                    </option>
                  ))}
                </Select>
              </div>
            )}

            {(isAdmin || isKS) && (
              <div>
                <label className="block text-sm font-medium mb-1">Pegawai</label>
                <Select
                  value={pegawaiId?.toString() ?? ''}
                  onChange={(e: React.ChangeEvent<HTMLSelectElement>) =>
                    setPegawaiId(Number(e.target.value) || null)
                  }
                  disabled={loading}
                >
                  <option value="">Semua Pegawai</option>
                  {pegawais.map((p) => (
                    <option key={p.id} value={p.id}>
                      {p.nama} ({p.nip_nik})
                    </option>
                  ))}
                </Select>
              </div>
            )}

            <div>
              <label className="block text-sm font-medium mb-1">Cari Pegawai</label>
              <Input
                type="text"
                placeholder="Nama / NIP/NIK..."
                value={search}
                onChange={(e: React.ChangeEvent<HTMLInputElement>) => setSearch(e.target.value)}
                disabled={loading}
              />
            </div>

            <div>
              <label className="block text-sm font-medium mb-1">Urutkan Berdasarkan</label>
              <div className="flex gap-2">
                <Select
                  value={sortBy}
                  onChange={(e: React.ChangeEvent<HTMLSelectElement>) =>
                    setSortBy(e.target.value as SortBy)
                  }
                  disabled={loading}
                  className="flex-1"
                >
                  <option value="nama">Nama</option>
                  <option value="nip_nik">NIP/NIK</option>
                  <option value="sekolah_nama">Sekolah</option>
                  <option value="honor_jp">Honor JP</option>
                  <option value="honor_kehadiran">Honor Kehadiran</option>
                  <option value="honor_jabatan">Honor Jabatan</option>
                  <option value="honor_wali_kelas">Honor Wali Kelas</option>
                  <option value="total_insentif">Total Insentif</option>
                  <option value="total_honor">Total Honor</option>
                </Select>
                <Select
                  value={sortDir}
                  onChange={(e: React.ChangeEvent<HTMLSelectElement>) =>
                    setSortDir(e.target.value as 'asc' | 'desc')
                  }
                  disabled={loading}
                  className="w-24"
                >
                  <option value="asc">A-Z</option>
                  <option value="desc">Z-A</option>
                </Select>
              </div>
            </div>

            <div className="flex items-end">
              <Button
                onClick={handleExportCsv}
                disabled={loading || exporting || !periodeHonorId || rows.length === 0}
                variant="secondary"
              >
                {exporting ? 'Mengunduh...' : 'Export CSV'}
              </Button>
            </div>
          </div>
        </div>

        {error && (
          <div style={{ marginBottom: '1rem' }}>
            <Alert type="error">{error}</Alert>
          </div>
        )}

        {!periodeHonorId && !loading && (
          <div style={{ marginBottom: '1rem' }}>
            <Alert type="info">Silakan pilih periode honor untuk melihat laporan</Alert>
          </div>
        )}

        {loading ? (
          <div className="text-center py-12 text-gray-500">Loading...</div>
        ) : rows.length === 0 && periodeHonorId ? (
          <div className="text-center py-12 text-gray-500">
            <p>Tidak ada data laporan honorarium</p>
            <p className="text-sm mt-2">Coba ubah filter atau periode yang dipilih</p>
          </div>
        ) : rows.length > 0 ? (
          <>
            <div className="overflow-x-auto">
              <Table>
                <thead>
                  <tr>
                    <Th>No</Th>
                    <Th>NIP/NIK</Th>
                    <Th>Nama Pegawai</Th>
                    {(isAdmin || isKS) && <Th>Sekolah</Th>}
                    <Th>Jabatan</Th>
                    <Th style={{ textAlign: 'center' }}>Status</Th>
                    <Th style={{ textAlign: 'right' }}>Total JP</Th>
                    <Th style={{ textAlign: 'right' }}>Honor JP</Th>
                    <Th style={{ textAlign: 'center' }}>Hadir</Th>
                    <Th style={{ textAlign: 'right' }}>Honor Kehadiran</Th>
                    <Th style={{ textAlign: 'right' }}>Honor Jabatan</Th>
                    <Th style={{ textAlign: 'right' }}>Honor Wali Kelas</Th>
                    <Th style={{ textAlign: 'right' }}>Total Insentif</Th>
                    <Th style={{ textAlign: 'right' }}>Total Honor</Th>
                  </tr>
                </thead>
                <tbody>
                  {rows.map((row, idx) => (
                    <tr key={row.pegawai_id}>
                      <Td>{(meta.page - 1) * meta.limit + idx + 1}</Td>
                      <Td className="font-mono text-sm">{row.nip_nik}</Td>
                      <Td className="font-medium">{row.nama}</Td>
                      {(isAdmin || isKS) && (
                        <Td className="text-sm text-gray-600">{row.sekolah_nama}</Td>
                      )}
                      <Td className="text-sm text-gray-600">{row.jabatan_nama}</Td>
                      <Td className="text-center">
                        <Badge color={row.status_slip === 'final' ? 'green' : 'gray'}>
                          {row.status_slip}
                        </Badge>
                      </Td>
                      <Td className="text-right">{row.total_jp}</Td>
                      <Td className="text-right font-mono text-sm">{formatRupiah(row.honor_jp)}</Td>
                      <Td className="text-center">{row.hari_hadir}</Td>
                      <Td className="text-right font-mono text-sm">
                        {formatRupiah(row.honor_kehadiran)}
                      </Td>
                      <Td className="text-right font-mono text-sm">
                        {formatRupiah(row.honor_jabatan)}
                      </Td>
                      <Td className="text-right font-mono text-sm">
                        {formatRupiah(row.honor_wali_kelas)}
                      </Td>
                      <Td className="text-right font-mono text-sm">
                        {formatRupiah(row.total_insentif)}
                      </Td>
                      <Td className="text-right font-mono text-sm font-semibold">
                        {formatRupiah(row.total_honor)}
                      </Td>
                    </tr>
                  ))}
                </tbody>
                <tfoot>
                  <tr style={{ borderTop: '2px solid #e5e7eb' }}>
                    <Th>{''}</Th>
                    <Th>{''}</Th>
                    <Th>GRAND TOTAL ({grandTotal.jumlah_pegawai} pegawai)</Th>
                    {(isAdmin || isKS) && <Th>{''}</Th>}
                    <Th>{''}</Th>
                    <Th>{''}</Th>
                    <Th>{''}</Th>
                    <Th style={{ textAlign: 'right' }}>{formatRupiah(grandTotal.honor_jp)}</Th>
                    <Th> </Th>
                    <Th style={{ textAlign: 'right' }}>
                      {formatRupiah(grandTotal.honor_kehadiran)}
                    </Th>
                    <Th style={{ textAlign: 'right' }}>{formatRupiah(grandTotal.honor_jabatan)}</Th>
                    <Th style={{ textAlign: 'right' }}>
                      {formatRupiah(grandTotal.honor_wali_kelas)}
                    </Th>
                    <Th style={{ textAlign: 'right' }}>
                      {formatRupiah(grandTotal.total_insentif)}
                    </Th>
                    <Th style={{ textAlign: 'right' }}>{formatRupiah(grandTotal.total_honor)}</Th>
                  </tr>
                </tfoot>
              </Table>
            </div>

            <div className="mt-4 flex justify-between items-center">
              <p className="text-sm text-gray-600">
                Menampilkan {(meta.page - 1) * meta.limit + 1} -{' '}
                {Math.min(meta.page * meta.limit, meta.total)} dari {meta.total} data
              </p>
              <Pagination
                page={meta.page}
                totalPages={Math.ceil(meta.total / meta.limit)}
                onChange={load}
              />
            </div>
          </>
        ) : null}
      </Card>
    </AdminLayout>
  );
}
